/**
 * Particle System Animation Module
 *
 * Provides ambient particles during loading and burst effects during transitions.
 * Features:
 * - Particles spawn from candle flames during loop
 * - Float upward with slight drift
 * - Fade out as they rise
 * - Golden/warm color variations
 * - Used heavily during exit transition
 */

import { LOADER_COLORS, type AnimationModule, type AnimationPhase, type Particle } from '../types';
import { lerp, clamp, fbmNoise } from './AnimationEngine';
import { random, randomRange, randomPick } from '@/utils/random';

interface ParticleEmitter {
  x: number;
  y: number;
  rate: number; // Particles per second
  active: boolean;
}

// Particle color palette
const PARTICLE_COLORS = [
  LOADER_COLORS.gold,
  LOADER_COLORS.goldBright,
  LOADER_COLORS.amber,
  LOADER_COLORS.flameMiddle,
  LOADER_COLORS.primaryLight,
];

export class ParticleSystemAnimation implements AnimationModule {
  id = 'particle-system';

  private width = 0;
  private height = 0;

  // Particles
  private particles: Particle[] = [];
  private maxParticles = 100;

  // Emitters (positioned at candle flames)
  private emitters: ParticleEmitter[] = [];

  // Animation state
  private time = 0;
  private isExiting = false;

  // Emission timing
  private emissionAccumulator = 0;

  init(_ctx: CanvasRenderingContext2D, width: number, height: number): void {
    this.resize(width, height);
  }

  resize(width: number, height: number): void {
    this.width = width;
    this.height = height;

    // Set up emitters at candle flame positions - matching candle positioning
    const minDimension = Math.min(width, height);
    const scale = 0.75; // Match candle scale (25% larger)
    const candleHeight = minDimension * 0.16 * scale;
    const flameHeight = candleHeight * 0.45;

    // Match candle spacing calculation
    const scrollWidth = minDimension * 0.5 * scale;
    const handleWidth = scrollWidth * 0.14;
    const candleWidth = minDimension * 0.022 * scale;
    const spacing = (scrollWidth * 0.7) / 2 + handleWidth + candleWidth * 1.5;

    const centerX = width / 2;
    const centerY = height / 2 + 5;
    const candleBaseY = centerY + candleHeight * 0.3;
    const flameY = candleBaseY - candleHeight - flameHeight * 0.3;

    this.emitters = [
      {
        x: centerX - spacing,
        y: flameY,
        rate: 2, // Particles per second
        active: true, // Start active
      },
      {
        x: centerX + spacing,
        y: flameY,
        rate: 2,
        active: true, // Start active
      },
    ];
  }

  update(deltaTime: number, phase: AnimationPhase, progress: number): void {
    const dt = deltaTime / 1000;
    this.time += dt;

    switch (phase) {
      case 'intro':
        // Start emitting after flames appear
        this.emitters.forEach((emitter) => {
          emitter.active = progress > 0.5;
        });
        break;

      case 'loop':
        // Normal emission rate
        this.emitters.forEach((emitter) => {
          emitter.active = true;
          emitter.rate = 3;
        });
        break;

      case 'exit':
        if (!this.isExiting) {
          this.isExiting = true;
          this.createExitBurst();
        }
        // Increase emission rate during exit
        this.emitters.forEach((emitter) => {
          emitter.active = progress < 0.5;
          emitter.rate = lerp(8, 0, progress);
        });
        break;

      case 'complete':
        this.emitters.forEach((emitter) => {
          emitter.active = false;
        });
        break;
    }

    // Emit new particles
    this.emitParticles(dt);

    // Update existing particles
    this.updateParticles(dt);
  }

  private emitParticles(dt: number): void {
    this.emitters.forEach((emitter) => {
      if (!emitter.active) return;

      this.emissionAccumulator += emitter.rate * dt;

      while (this.emissionAccumulator >= 1 && this.particles.length < this.maxParticles) {
        this.emissionAccumulator -= 1;
        this.createParticle(emitter.x, emitter.y, 'ambient');
      }
    });
  }

  private createParticle(x: number, y: number, type: 'ambient' | 'burst' = 'ambient'): void {
    const color = randomPick(PARTICLE_COLORS);

    if (type === 'ambient') {
      this.particles.push({
        x: x + randomRange(-10, 10),
        y: y + random() * 10,
        vx: randomRange(-7.5, 7.5),
        vy: -20 - random() * 30,
        life: 1,
        maxLife: 1,
        size: randomRange(1.5, 4),
        color,
        alpha: randomRange(0.8, 1),
      });
    } else {
      const angle = random() * Math.PI * 2;
      const speed = randomRange(50, 150);

      this.particles.push({
        x: x + randomRange(-15, 15),
        y: y + randomRange(-10, 10),
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 30,
        life: 1,
        maxLife: 1,
        size: randomRange(2, 6),
        color,
        alpha: 1,
      });
    }
  }

  private createExitBurst(): void {
    // Create burst of particles from each emitter
    this.emitters.forEach((emitter) => {
      for (let i = 0; i < 30; i++) {
        this.createParticle(emitter.x, emitter.y, 'burst');
      }
    });

    // Also create particles across the screen for a more dramatic effect
    for (let i = 0; i < 20; i++) {
      const x = this.width * 0.2 + random() * this.width * 0.6;
      const y = this.height * 0.3 + random() * this.height * 0.4;
      this.createParticle(x, y, 'burst');
    }
  }

  private updateParticles(dt: number): void {
    this.particles.forEach((particle) => {
      // Apply noise-based drift for organic movement
      const noise = fbmNoise(this.time * 2 + particle.x * 0.01, 2, particle.y);
      const drift = (noise - 0.5) * 30;

      // Update position
      particle.x += (particle.vx + drift) * dt;
      particle.y += particle.vy * dt;

      // Slow down velocity
      particle.vx *= 0.98;
      particle.vy *= 0.98;

      // Slight upward force (heat rising)
      particle.vy -= 5 * dt;

      // Decrease life
      const lifeDecay = this.isExiting ? 1.5 : 0.8;
      particle.life -= dt * lifeDecay;

      // Update alpha based on life
      particle.alpha = clamp(particle.life / particle.maxLife, 0, 1);

      // Shrink as life decreases
      particle.size *= 0.995;
    });

    // Remove dead particles
    this.particles = this.particles.filter((p) => p.life > 0 && p.size > 0.5);
  }

  render(ctx: CanvasRenderingContext2D): void {
    ctx.save();

    this.particles.forEach((particle) => {
      this.renderParticle(ctx, particle);
    });

    ctx.restore();
  }

  private renderParticle(ctx: CanvasRenderingContext2D, particle: Particle): void {
    if (particle.alpha <= 0.01) return;

    ctx.save();
    ctx.globalAlpha = particle.alpha;

    // Create radial gradient for soft glow effect
    const gradient = ctx.createRadialGradient(
      particle.x,
      particle.y,
      0,
      particle.x,
      particle.y,
      particle.size
    );

    gradient.addColorStop(0, particle.color);
    gradient.addColorStop(0.5, this.adjustColorAlpha(particle.color, 0.5));
    gradient.addColorStop(1, this.adjustColorAlpha(particle.color, 0));

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  private adjustColorAlpha(color: string, alpha: number): string {
    if (color.startsWith('#')) {
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    return color;
  }

  triggerExit(): void {
    this.isExiting = true;
    this.createExitBurst();
  }

  destroy(): void {
    this.particles = [];
    this.emitters = [];
  }
}
